import { Button, Center, Loader, NumberInput, TextInput } from '@mantine/core';
import {
  formatDuration,
  POST_TYPES,
  type EventIdentity,
  type PostType,
  type ShiftPlanning,
} from '@reditelji/shared';
import { Fragment, useEffect, useState } from 'react';
import { useEvent, useEventMutation, useShiftPlanningMutation } from '../api/hooks';
import { useSession } from '../api/session';
import type { EventInfo } from '../api/types';
import { sameTrimmed, useDraft, type FieldSame } from '../lib/draft';
import { UsersCard } from './UsersCard';
import './settings.css';

const TYPE_LABEL: Record<PostType, string> = {
  trasa: 'Trasa',
  'parkirišče': 'Parkirišče',
  'štartno-ciljni': 'Štartno-ciljni',
};

/**
 * Coordinator settings, in two cards with separate saves because they carry
 * different weight: the contact block is printed on every volunteer's briefing
 * and a change reaches the ones already sent, while the autofill settings shape
 * the *next* proposal only and move nothing already rostered. Neither is a
 * material change (SPEC §1.1F), so no confirmation resets either way.
 */
export function SettingsPage() {
  const event = useEvent();
  // Each card asks for what it writes (SPEC §1.1L).
  const { can } = useSession();
  const planning = event.data?.shiftPlanning;

  if (event.isPending) {
    return (
      <Center mih="60vh">
        <Loader />
      </Center>
    );
  }
  if (!planning || !event.data) return null;
  return (
    <div className="st-page">
      <div className="st-wrap">
        <div>
          <div className="st-title">Nastavitve</div>
          <div className="st-lede">
            Kdo je dogodek za prostovoljca in po katerih pravilih <em>Samodejno zapolni</em>{' '}
            sestavi predlog razporeda.
          </div>
        </div>

        {/* Both forms draft over the server's own values: whatever it last
            confirmed is the baseline, so "Shrani" settles once the write
            lands. */}
        {can('settings:write') ? (
          <>
            <IdentityForm event={event.data} />
            <PlanningForm planning={planning} />
          </>
        ) : null}
        {can('access:manage') ? <UsersCard /> : null}
      </div>
    </div>
  );
}

/** The confirmation is a moment, not a state. */
function useSavedFlash() {
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 2500);
    return () => clearTimeout(t);
  }, [saved]);
  return [saved, () => setSaved(true)] as const;
}

/** The identity as the form edits it: every field a string, no deadline as ''. */
type IdentityDraft = Required<Omit<EventIdentity, 'answerDeadline'>> & { answerDeadline: string };

/** zod trims these before storing them. */
const IDENTITY_SAME: FieldSame<IdentityDraft> = {
  name: sameTrimmed,
  opsPhone: sameTrimmed,
  opsName: sameTrimmed,
  mailbox: sameTrimmed,
  whatsappName: sameTrimmed,
  whatsappUrl: sameTrimmed,
  emergencyPhone: sameTrimmed,
};

const WHATSAPP_INVITE = /^https:\/\/chat\.whatsapp\.com\/[A-Za-z0-9]+(\?\S*)?$/;

/**
 * The event's contact identity, shown beside the briefing block it prints to.
 *
 * A coordinator never opens a volunteer's briefing, so these fields are
 * otherwise invisible from here: nothing on this screen would tell you that an
 * empty ops phone leaves a dead `tel:` link on every briefing card. The preview
 * is what makes that visible.
 */
function IdentityForm({ event }: { event: EventInfo }) {
  const base: IdentityDraft = {
    name: event.name,
    opsPhone: event.opsPhone,
    opsName: event.opsName,
    mailbox: event.mailbox,
    whatsappName: event.whatsappName,
    whatsappUrl: event.whatsappUrl,
    emergencyPhone: event.emergencyPhone,
    answerDeadline: event.answerDeadline ?? '',
  };
  const { draft, setDraft, dirty, reset } = useDraft(base, IDENTITY_SAME);
  const [saved, flashSaved] = useSavedFlash();
  const save = useEventMutation();

  const set = <K extends keyof IdentityDraft>(key: K, value: IdentityDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  // Mirrors the zod body, so Shrani never sends something the API rejects.
  const noName = draft.name.trim() === '';
  const badMailbox =
    draft.mailbox.trim() !== '' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(draft.mailbox.trim());
  const noPhone = draft.opsPhone.trim() === '';
  const badWhatsapp = draft.whatsappUrl.trim() !== '' && !WHATSAPP_INVITE.test(draft.whatsappUrl.trim());

  return (
    <div className="st-card">
      <div className="st-eyebrow">KONTAKTI NA BRIFINGU</div>
      <div className="st-hint" style={{ marginBottom: 14 }}>
        Kdo je dogodek, koga prostovoljec pokliče in kje je skupina WhatsApp — izpiše se na brifingu
        vsakega prostovoljca. Sprememba velja takoj, tudi za brifinge, ki so že poslani.
      </div>

      <div className="st-identity">
        <div className="st-fields">
          <div>
            <TextInput
              size="sm"
              label="Ime dogodka"
              placeholder="Julian Alps Trail Run 2026"
              error={noName}
              value={draft.name}
              onChange={(e) => set('name', e.currentTarget.value)}
            />
            <div className="st-hint">V glavi aplikacije in kot ime stika na napotnici.</div>
            {noName ? <div className="st-error">Ime je obvezno.</div> : null}
          </div>

          <div>
            <TextInput
              size="sm"
              label="Kontakt med dogodkom"
              placeholder="Peter"
              value={draft.opsName}
              onChange={(e) => set('opsName', e.currentTarget.value)}
            />
            <div className="st-hint">Ime osebe, ki jo prostovoljec pokliče (»Pokličite Petra«).</div>
          </div>

          <div>
            <TextInput
              size="sm"
              label="Telefon med dogodkom"
              placeholder="+386 41 000 000"
              inputMode="tel"
              value={draft.opsPhone}
              onChange={(e) => set('opsPhone', e.currentTarget.value)}
            />
            <div className="st-hint">
              Številka, ki jo prostovoljec pokliče med dogodkom. Na brifingu je klicna povezava.
            </div>
            {noPhone ? (
              <div className="st-warn">
                Brez številke brifing ne ponudi klica — ostane le 112 in skupina WhatsApp.
              </div>
            ) : null}
          </div>

          <div>
            <TextInput
              size="sm"
              label="Številka za nujne primere"
              placeholder="+386 30 290 089"
              inputMode="tel"
              value={draft.emergencyPhone}
              onChange={(e) => set('emergencyPhone', e.currentTarget.value)}
            />
            <div className="st-hint">
              Uradna številka organizatorja iz vodnika po dirki (tudi na štartnih številkah). Na brifingu
              jo prostovoljec pokliče ob poškodbi ali odstopu tekača.
            </div>
            {draft.emergencyPhone.trim() === '' ? (
              <div className="st-warn">Brez številke brifing pri poškodbah in odstopih navaja le 112 in kontakt med dogodkom.</div>
            ) : null}
          </div>

          <div>
            <TextInput
              size="sm"
              label="Skupina WhatsApp"
              placeholder="JAT26 Redarji"
              value={draft.whatsappName}
              onChange={(e) => set('whatsappName', e.currentTarget.value)}
            />
          </div>

          <div>
            <TextInput
              size="sm"
              label="Povezava za pridružitev"
              placeholder="https://chat.whatsapp.com/…"
              inputMode="url"
              error={badWhatsapp}
              value={draft.whatsappUrl}
              onChange={(e) => set('whatsappUrl', e.currentTarget.value)}
            />
            <div className="st-hint">V WhatsAppu: podatki o skupini → Povabi prek povezave.</div>
            {badWhatsapp ? (
              <div className="st-error">Povezava mora biti vabilo v skupino (https://chat.whatsapp.com/…).</div>
            ) : null}
          </div>

          <div>
            <TextInput
              size="sm"
              type="date"
              label="Rok za odgovor"
              value={draft.answerDeadline}
              onChange={(e) => set('answerDeadline', e.currentTarget.value)}
            />
            <div className="st-hint">Do kdaj prosimo za potrditev izmen. Prazno: brifing roka ne navaja.</div>
          </div>
        </div>

        <ContactPreview name={draft.opsName} phone={draft.opsPhone} whatsapp={draft.whatsappName} />
      </div>

      {/* Mail coming back the other way, so it sits apart from the preview
          rather than under a card that never shows it. */}
      <div className="st-eyebrow">DOHODNA POŠTA</div>
      <div className="st-mailbox">
        <TextInput
          size="sm"
          label="Poštni predal"
          placeholder="reditelji@primer.si"
          inputMode="email"
          error={badMailbox}
          value={draft.mailbox}
          onChange={(e) => set('mailbox', e.currentTarget.value)}
        />
        <div className="st-hint">
          Naslov, na katerega prostovoljci odgovarjajo. Iz njega aplikacija pobira sporočila in jih
          pripne pravemu prostovoljcu; česar ne prepozna, pristane med nerazvrščeno pošto.
        </div>
        {badMailbox ? <div className="st-error">Vpiši veljaven e-naslov.</div> : null}
      </div>

      <div className="st-actions">
        {saved ? <div className="st-saved">Shranjeno.</div> : null}
        <Button variant="default" disabled={!dirty || save.isPending} onClick={reset}>
          Razveljavi
        </Button>
        <Button
          disabled={!dirty || noName || badMailbox || badWhatsapp}
          loading={save.isPending}
          onClick={() =>
            save.mutate(
              {
                name: draft.name.trim(),
                opsPhone: draft.opsPhone.trim(),
                opsName: draft.opsName.trim(),
                mailbox: draft.mailbox.trim(),
                whatsappName: draft.whatsappName.trim(),
                whatsappUrl: draft.whatsappUrl.trim(),
                emergencyPhone: draft.emergencyPhone.trim(),
                answerDeadline: draft.answerDeadline || null,
              },
              { onSuccess: flashSaved },
            )
          }
        >
          Shrani
        </Button>
      </div>
    </div>
  );
}

/**
 * The briefing's contact block as a volunteer reads it, following the draft.
 *
 * A likeness rather than the briefing's own component: it is two rows of static
 * markup, and sharing one would tie this screen to the briefing's layout and
 * make either harder to change.
 */
function ContactPreview({ name, phone, whatsapp }: { name: string; phone: string; whatsapp: string }) {
  const missing = phone.trim() === '';
  return (
    <figure className="st-preview">
      <figcaption className="st-preview-cap">Brifing prostovoljca</figcaption>
      <div className="st-preview-card">
        <div className="st-preview-title">Kontakti</div>
        {whatsapp.trim() ? (
          <div className="st-preview-row">
            <div>
              <div className="st-preview-lbl">med dogodkom · WhatsApp</div>
              <div className="st-preview-name">Skupina »{whatsapp.trim()}«</div>
            </div>
          </div>
        ) : null}
        <div className="st-preview-row">
          <div>
            <div className="st-preview-lbl">med dogodkom · telefon</div>
            <div className="st-preview-name">{name.trim() || '—'}</div>
          </div>
          <div className={`st-preview-tel${missing ? ' st-preview-tel--missing' : ''}`}>
            {missing ? 'ni številke' : phone}
          </div>
        </div>
        <div className="st-preview-row">
          <div className="st-preview-lbl">reševalci in gasilci</div>
          <div className="st-preview-tel">112</div>
        </div>
      </div>
    </figure>
  );
}

function PlanningForm({ planning }: { planning: ShiftPlanning }) {
  // Stored as jsonb, which comes back in its own key order.
  const { draft, setDraft, dirty, reset } = useDraft(planning, {});
  const [saved, flashSaved] = useSavedFlash();
  const save = useShiftPlanningMutation();

  const set = <K extends keyof ShiftPlanning>(key: K, value: ShiftPlanning[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const setLengths = (type: PostType, key: 'targetMin' | 'maxMin', value: number) =>
    setDraft((d) => ({
      ...d,
      byType: { ...d.byType, [type]: { ...d.byType[type], [key]: value } },
    }));

  // Mirrors the two zod refinements, so Shrani never sends a body the API rejects.
  const badLengths = POST_TYPES.filter((t) => draft.byType[t].maxMin < draft.byType[t].targetMin);
  const badDay = draft.softDailyMin > draft.maxDailyMin;
  const badFloor = POST_TYPES.some((t) => draft.minPieceMin > draft.byType[t].maxMin);

  return (
    <div className="st-card">
      <div className="st-eyebrow">DOLŽINA IZMENE PO TIPU MESTA</div>
      <div className="st-hint" style={{ marginBottom: 12 }}>
        Nepokrito okno se razreže na izmene te dolžine. Rez se poravna na obstoječo predajo ali
        na mejo termina iz prijavnice, če je ta dovolj blizu.
      </div>

      <div className="st-typetable">
        <div />
        <div className="st-colhead">ciljna</div>
        <div className="st-colhead">največ</div>
        {POST_TYPES.map((type) => (
          <Fragment key={type}>
            <div className="st-typename">{TYPE_LABEL[type]}</div>
            <NumberInput
              size="sm"
              aria-label={`${TYPE_LABEL[type]} — ciljna dolžina izmene`}
              suffix=" min"
              step={15}
              min={15}
              max={720}
              value={draft.byType[type].targetMin}
              onChange={(v) => setLengths(type, 'targetMin', Number(v) || 0)}
            />
            <NumberInput
              size="sm"
              aria-label={`${TYPE_LABEL[type]} — najdaljša izmena`}
              suffix=" min"
              step={15}
              min={15}
              max={720}
              error={draft.byType[type].maxMin < draft.byType[type].targetMin}
              value={draft.byType[type].maxMin}
              onChange={(v) => setLengths(type, 'maxMin', Number(v) || 0)}
            />
          </Fragment>
        ))}
      </div>
      {badLengths.length > 0 ? (
        <div className="st-error" style={{ marginTop: 10 }}>
          Največja izmena ne sme biti krajša od ciljne (
          {badLengths.map((t) => TYPE_LABEL[t]).join(', ')}).
        </div>
      ) : null}

      <div className="st-eyebrow">DAN PROSTOVOLJCA</div>
      <div className="st-grid">
        <div>
          <NumberInput
            size="sm"
            label="Priporočeno na osebo/dan"
            suffix=" min"
            step={30}
            min={60}
            max={1440}
            error={badDay}
            value={draft.softDailyMin}
            onChange={(v) => set('softDailyMin', Number(v) || 0)}
          />
          <div className="st-hint">
            Skupaj čez ves dan, ne dolžina ene izmene. Predlog vsem najprej razdeli toliko dela
            in šele nato komu podaljša dan — tako pride na vrsto več prijavljenih.
            {draft.softDailyMin === draft.maxDailyMin
              ? ' Enako kot največ: predlog bo raje polnil posameznika do konca.'
              : ''}
          </div>
        </div>
        <div>
          <NumberInput
            size="sm"
            label="Največ na osebo/dan"
            suffix=" min"
            step={30}
            min={60}
            max={1440}
            value={draft.maxDailyMin}
            onChange={(v) => set('maxDailyMin', Number(v) || 0)}
          />
          <div className="st-hint">
            Trda meja — predlog je ne prekorači nikoli. Do nje seže samo za vrzel, ki bi sicer
            ostala nepokrita. Pri {formatDuration(draft.maxDailyMin)} ostane pod pragom
            dolgega dneva (10 h).
          </div>
        </div>
        <div>
          <NumberInput
            size="sm"
            label="Najkrajši odsek"
            suffix=" min"
            step={15}
            min={5}
            max={240}
            error={badFloor}
            value={draft.minPieceMin}
            onChange={(v) => set('minPieceMin', Number(v) || 0)}
          />
          <div className="st-hint">
            Krajšega kosa predlog ne izreže. Naravno kratko okno ostane celo.
          </div>
          {badFloor ? (
            <div className="st-error">
              Ne sme presegati najdaljše izmene.
            </div>
          ) : null}
        </div>
        <div>
          <NumberInput
            size="sm"
            label="Odstopanje pri rezu"
            suffix=" min"
            step={15}
            min={0}
            max={240}
            value={draft.alignToleranceMin}
            onChange={(v) => set('alignToleranceMin', Number(v) || 0)}
          />
          <div className="st-hint">
            Koliko sme rez odstopati od ciljne dolžine, da pade na obstoječo predajo ali mejo
            termina. Pri 0 reže točno na ciljno dolžino.
          </div>
        </div>
      </div>
      {badDay ? (
        <div className="st-error" style={{ marginTop: 10 }}>
          Priporočeni dan ne sme biti daljši od največjega.
        </div>
      ) : null}

      <div className="st-actions">
        {saved ? <div className="st-saved">Shranjeno.</div> : null}
        <Button
          variant="default"
          disabled={!dirty || save.isPending}
          onClick={reset}
        >
          Razveljavi
        </Button>
        <Button
          disabled={!dirty || badLengths.length > 0 || badDay || badFloor}
          loading={save.isPending}
          onClick={() => save.mutate(draft, { onSuccess: flashSaved })}
        >
          Shrani
        </Button>
      </div>
    </div>
  );
}
